import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Loader, Modal } from "semantic-ui-react";

import { useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { EditQuestionActions, EditQuestionForm, QuestionElements } from "modules/QuestionsDashboard/components/EditQuestionModal/components";
import "modules/QuestionsDashboard/components/EditQuestionModal/EditQuestionModal.scss";
import { useUpdateQuestionMutation } from "modules/QuestionsDashboard/components/EditQuestionModal/hooks";
import { useQuestionDetailsQuery } from "modules/QuestionsDashboard/hooks";
import { isAdmin } from "utils/common";

import type { Question, QuestionID } from "models";

type EditQuestionModalProps = {
  questionID?: QuestionID;
  onClose: () => void;
  onOpenQuestion: (questionID: QuestionID) => void;
};

const FormHelper = createFormHelper<Question>();

export const EditQuestionModal = ({ questionID, onClose, onOpenQuestion }: EditQuestionModalProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { data: question, isFetching } = useQuestionDetailsQuery(questionID);
  const { mutateAsync: updateQuestion, isPending } = useUpdateQuestionMutation();

  const canUpdate = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Questions);

  const canEdit = canUpdate && (!!question?.dealer_id || isAdmin(user?.role_id ?? 0));

  const handleSubmit = async (values: Question) => {
    const evhcPercentTotal = values.evhc_labor_percent + values.evhc_part_percent + values.evhc_tire_percent + values.evhc_external_percent;

    if (!values.title?.trim()) return toast.error(t("v8_title_is_required", "Title is required"));
    if (evhcPercentTotal > 100) return toast.error(t("v8_evhc_percentages_exceeded", "The EVHC percent values must not exceed 100%"));

    await updateQuestion({
      ...values,
      question_elements: values.question_elements?.map((questionElement, elementIdx) => ({ ...questionElement, ordering: elementIdx }))
    });
    onClose();
  };

  return (
    <Modal size="large" className="EditQuestionModal" open={!!questionID} onClose={onClose} closeOnDimmerClick={false}>
      <Modal.Header>{t("v8_edit_question", "Edit Question")}</Modal.Header>

      {(isFetching || !question) && (
        <Modal.Content>
          <Loader active inline="centered" />
        </Modal.Content>
      )}

      {!isFetching && question && (
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            ...question,
            tag_ids: question.tags?.map(tag => tag.id) ?? [],
            question_elements: question.question_elements ?? [],
            question_options: question.question_options ?? []
          }}
        >
          <Modal.Content scrolling>
            <EditQuestionForm canEdit={canEdit} />
            <QuestionElements canEdit={canEdit} />
          </Modal.Content>

          <EditQuestionActions question={question} canEdit={canEdit} isSubmitting={isPending} onClose={onClose} onOpenQuestion={onOpenQuestion} />
        </FormHelper.Form>
      )}
    </Modal>
  );
};
